import React, { forwardRef, useCallback, useContext, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInputField from '../FormInputField';
import Button, { buttonType } from '../../components/Button';
import Loader from '../Loader';
import Alert from '../Alert';
import { alertType } from '../Alert/alertType';
import { useLabels } from '../../hooks';
import { UserContext } from '../../contexts';
import { userApi } from '../../api';
import { RefreshIcon, SmsIcon, PhoneIcon } from './CodeOptionIcons';
import { formatString } from '../../utils/helpers';
import styles from './LoginForm.module.scss';

const MigrationAlert = forwardRef((props, ref) => {
    const { getLabel, getCognitoApiError } = useLabels();
    const navigate = useNavigate();
    const { signUp, signIn, getJwtToken, resendConfirmationAccountCode, confirmAccount, sendResetPasswordCode, confirmPassword, pingUserData } = useContext(UserContext);

    const alertRef = useRef();
    const passwordRef = useRef();
    const confirmPasswordRef = useRef();
    const [error, setError] = useState();
    const [loading, setLoading] = useState(false);
    const [isShown, setIsShown] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [view, setView] = useState('form'); // form | confirm | success | reset_confirm
    const [confirmCode, setConfirmCode] = useState('');
    const [isPasswordReset, setIsPasswordReset] = useState(false);
    const [resendSent, setResendSent] = useState(false);
    const [smsMaskedPhone, setSmsMaskedPhone] = useState(null);
    const [voiceMaskedPhone, setVoiceMaskedPhone] = useState(null);
    const [smsSent, setSmsSent] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [smsError, setSmsError] = useState(null);
    const [voiceCallSent, setVoiceCallSent] = useState(false);
    const [voiceCallSending, setVoiceCallSending] = useState(false);
    const [voiceCallError, setVoiceCallError] = useState(null);
    // keep context separate from React state to avoid stale/async issues
    const ctxRef = useRef({ email: '', oldPassword: '', oldUser: null, newPassword: '', temporaryPassword: '' });

    const confirmPasswordValidation = useCallback((password) => {
        return password === passwordRef.current?.getValue() ? '' : getLabel('migration_password_mismatch_error');
    }, [getLabel]);

    // Shared fallback for UNCONFIRMED users: sendResetPasswordCode fails (no verified email),
    // so resend the original confirmation code and go to confirm view.
    const handleUnconfirmedFallback = useCallback(async (effectiveEmail, newPassword) => {
        try {
            ctxRef.current.newPassword = newPassword;
            await resendConfirmationAccountCode(effectiveEmail, newPassword);
            setError(null);
            setView('confirm');
        } catch (e) {
            console.error('resendConfirmationAccountCode failed:', e);
            throw new Error(getLabel('migration_stuck_use_forgot_password'));
        }
    }, [getLabel, resendConfirmationAccountCode]);

    const migrate = useCallback(async (email, oldPassword, newPassword, oldUser) => {
        const effectiveEmail = (oldUser && oldUser.email ? oldUser.email : email || '').trim();

        // Handle password reset for already migrated users
        if (isPasswordReset) {
            try {
                await signUp({ email: effectiveEmail, password: newPassword });
                // User doesn't exist → normal migration confirm flow
                ctxRef.current.newPassword = newPassword;
                setError(null);
                setView('confirm');
                return;
            } catch (e) {
                if (e.code === 'UsernameExistsException' || e.code === 'AliasExistsException' || e.code === 'SignUpNotAuthorizedException') {
                    try {
                        ctxRef.current.temporaryPassword = newPassword;
                        await sendResetPasswordCode(effectiveEmail);
                        setError(null);
                        setView('reset_confirm');
                        return;
                    } catch {
                        await handleUnconfirmedFallback(effectiveEmail, newPassword);
                        return;
                    }
                } else {
                    throw new Error(getLabel('migration_change_password_error'));
                }
            }
        }

        // Normal migration flow
        try {
            const signUpPayload = { email: effectiveEmail, password: newPassword };
            if (oldUser) {
                if (oldUser.firstName) signUpPayload.firstName = oldUser.firstName;
                if (oldUser.lastName) signUpPayload.lastName = oldUser.lastName;
                if (oldUser.phone) signUpPayload.phone = oldUser.phone;
            }
            await signUp(signUpPayload);
            ctxRef.current.newPassword = newPassword;
            setError(null);
            setView('confirm');
            return;
        } catch (e) {
            if (e.code === 'UsernameExistsException' || e.code === 'AliasExistsException' || e.code === 'SignUpNotAuthorizedException') {
                // RMIL-826: User started migration before but never confirmed — Cognito has them
                // UNCONFIRMED with their original password. Try reset flow first; if that fails
                // (UNCONFIRMED users have no verified email), fall back to resending the
                // original confirmation code.
                try {
                    ctxRef.current.temporaryPassword = newPassword;
                    await sendResetPasswordCode(effectiveEmail);
                    setError(null);
                    setView('reset_confirm');
                    return;
                } catch {
                    await handleUnconfirmedFallback(effectiveEmail, newPassword);
                    return;
                }
            }
            throw new Error(getLabel('migration_change_password_error'));
        }
    }, [getLabel, signUp, sendResetPasswordCode, handleUnconfirmedFallback, isPasswordReset]);

    const onSubmit = useCallback(async (event) => {
        event?.preventDefault();
        let focusField = null;

        if (!passwordRef.current?.validation()) {
            focusField = focusField || passwordRef.current;
        }

        if (!confirmPasswordRef.current?.validation()) {
            focusField = focusField || confirmPasswordRef.current;
        }

        if (focusField) {
            focusField.focus();
            return;
        }

        const newPassword = passwordRef.current?.getValue();
        const confirmPassword = confirmPasswordRef.current?.getValue();
        if (newPassword !== confirmPassword) {
            setError({ message: getLabel('migration_password_mismatch_error') });
            confirmPasswordRef.current?.focus();
            return;
        }

        setError(null);
        setLoading(true);
        try {
            const { email, oldPassword, oldUser } = ctxRef.current;
            await migrate(email, oldPassword, newPassword, oldUser);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [getLabel, migrate]);

    const handleSendViaSms = useCallback(async () => {
        setSmsSending(true);
        setSmsError(null);
        try {
            const res = await userApi.SendSmsOtp(ctxRef.current.email);
            if (res?.success) setSmsSent(true);
            else setSmsError(getLabel('sms_no_phone'));
        } catch {
            setSmsError(getLabel('sms_no_phone'));
        } finally {
            setSmsSending(false);
        }
    }, [getLabel]);

    const handleSendViaVoice = useCallback(async () => {
        setVoiceCallSending(true);
        setVoiceCallError(null);
        try {
            const res = await userApi.SendVoiceOtp(ctxRef.current.email);
            if (res?.success) setVoiceCallSent(true);
            else if (res?.maskedPhone) setVoiceCallError(getLabel('voice_rate_limited'));
            else setVoiceCallError(getLabel('voice_no_phone'));
        } catch {
            setVoiceCallError(getLabel('voice_no_phone'));
        } finally {
            setVoiceCallSending(false);
        }
    }, [getLabel]);

    const handleResendCode = useCallback(async (apiFn) => {
        setSmsSent(false);
        setSmsError(null);
        setVoiceCallSent(false);
        setVoiceCallError(null);
        try {
            setLoading(true);
            await apiFn();
            setResendSent(true);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const renderFallbackButtons = useCallback(() => (
        <>
            {smsMaskedPhone && !smsSent && !voiceCallSent && (
                <button type="button" className={styles.codeOption} disabled={smsSending || voiceCallSending || loading} onClick={handleSendViaSms}>
                    <SmsIcon />
                    <span>{formatString(getLabel('sms_option_label'), smsMaskedPhone)}</span>
                </button>
            )}
            {smsSent && (
                <div style={{ fontSize: '14px', paddingTop: '8px' }}>{formatString(getLabel('sms_sent_to_phone'), smsMaskedPhone)}</div>
            )}
            {smsError && <div style={{ color: '#ff3333', fontSize: '14px' }}>{smsError}</div>}
            {voiceMaskedPhone && !smsSent && !voiceCallSent && (
                <button type="button" className={styles.codeOption} disabled={voiceCallSending || smsSending || loading} onClick={handleSendViaVoice}>
                    <PhoneIcon />
                    <span>{formatString(getLabel('voice_option_label'), voiceMaskedPhone)}</span>
                </button>
            )}
            {voiceCallSent && (
                <div style={{ fontSize: '14px', paddingTop: '8px' }}>{formatString(getLabel('voice_sent_to_phone'), voiceMaskedPhone)}</div>
            )}
            {voiceCallError && <div style={{ color: '#ff3333', fontSize: '14px' }}>{voiceCallError}</div>}
        </>
    ), [smsMaskedPhone, voiceMaskedPhone, smsSent, voiceCallSent, smsSending, voiceCallSending, loading, handleSendViaSms, handleSendViaVoice, smsError, voiceCallError, getLabel]);

    const renderAlert = useCallback(() => {
        if (!alertRef.current) return;
        if (view === 'success' || completed) {
            alertRef.current.show({
                type: alertType.alert,
                title: getLabel('migration_success_title'),
                text: getLabel('migration_success_text'),
                button_text: getLabel('migration_success_button'),
                onButtonClick: () => {
                    try { sessionStorage.removeItem('migrationInProgress'); } catch (_) {}
                    alertRef.current?.setShow(false);
                    setIsShown(false);
                    try {
                        const lang = getLabel('lang') || 'he';
                        const target = lang === 'he' ? '/' : `/${lang}`;
                        window.location.assign(target);
                    } catch (_) {}
                },
                preventCloseOnButton: false,
                content: null,
            });
            return;
        }

        if (view === 'confirm') {
            alertRef.current.show({
                type: alertType.alert,
                title: getLabel('migration_welcome_title'),
                text: `${getLabel('signup_form_confirm_account_by_email_without_address')}<br/>${getLabel('migration_spam_warning')}`,
                button_text: getLabel('migration_confirm_account_button'),
                onButtonClick: async () => {
                    if (!confirmCode || !confirmCode.trim()) {
                        setError({ message: getLabel('migration_confirm_code_required') });
                        return;
                    }
                    try {
                        setLoading(true);
                        try { sessionStorage.setItem('migrationInProgress', '1'); } catch (_) {}
                        if (smsSent || voiceCallSent) {
                            const res = await userApi.VerifySmsOtpAndConfirmAccount(ctxRef.current.email, confirmCode);
                            if (!res?.success) throw new Error(getLabel('otp_expired_or_invalid'));
                        } else {
                            await confirmAccount(confirmCode);
                        }
                        const { email, newPassword } = ctxRef.current;
                        
                        // Wait a moment for Cognito to process the confirmation
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        // Sign in with new password to obtain token, call migration, then refresh
                        try {
                            await signIn(email, newPassword || '');
                        } catch (signInError) {
                            if (signInError && signInError.code === 'UserNotConfirmedException') {
                                // Timing issue — user is confirmed but Cognito hasn't propagated yet
                                await new Promise(resolve => setTimeout(resolve, 1000));
                                await signIn(email, newPassword || '');
                            } else if (signInError && signInError.code === 'NotAuthorizedException') {
                                // Password mismatch: user entered a different password than their
                                // first migration attempt. User is now confirmed, so reset flow works.
                                ctxRef.current.temporaryPassword = newPassword;
                                await sendResetPasswordCode(email);
                                setError(null);
                                setConfirmCode('');
                                setResendSent(false);
                                setView('reset_confirm');
                                return;
                            } else {
                                throw signInError;
                            }
                        }
                        const token = getJwtToken ? getJwtToken() : '';
                        const res = await userApi.MigrateUserFromOldDB(token);
                        const isOk = (res === true) || (res && (res.success === true || res.status === 'Success' || res.status === 'AlreadyExists'));
                        if (!isOk) {
                            const message = (res && (res.errorMessage || res.message)) || 'backend-migration-failed';
                            throw new Error(message);
                        }
                        try { sessionStorage.removeItem('migrationInProgress'); } catch (_) {}
                        pingUserData();
                        alertRef.current?.setShow(false);
                        setIsShown(false);
                        const lang = getLabel('lang') || 'he';
                        navigate(lang === 'he' ? '/' : `/${lang}`);
                    } catch (err) {
                        setError(err);
                    } finally {
                        setLoading(false);
                    }
                },
                preventCloseOnButton: true,
                content: (
                    <div className={styles.migrationFildes}>
                        <FormInputField
                            id="migration_confirm_code_field"
                            label={getLabel('signup_form_confirm_account_code_field')}
                            placeholder={getLabel('signup_form_confirm_account_code_field')}
                            type="text"
                            required={true}
                            heContentDirection={'ltr'}
                            hePlaceholderDirection={'rtl'}
                            value={confirmCode}
                            autoComplete="one-time-code"
                            name="confirmation-code"
                            inputMode="numeric"
                            onChange={(e) => setConfirmCode(e.target.value)}
                        />
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: '#ff3333', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error) }} />
                        </div>
                        {loading && (
                            <div style={{ marginTop: '10px' }}>
                                <Loader />
                            </div>
                        )}
                        <div className={styles.codeOptions}>
                            <button
                                type="button"
                                className={styles.codeOption}
                                disabled={loading || resendSent}
                                onClick={() => handleResendCode(() => resendConfirmationAccountCode(ctxRef.current.email, ctxRef.current.newPassword))}
                            >
                                <RefreshIcon />
                                <span>{resendSent ? getLabel('migration_code_sent_confirmation') : getLabel('migration_resend_code_link')}</span>
                            </button>
                            {renderFallbackButtons()}
                        </div>
                    </div>
                )
            });
            return;
        }

        if (view === 'reset_confirm') {
            alertRef.current.show({
                type: alertType.alert,
                title: getLabel('migration_welcome_title'),
                text: `${getLabel('signup_form_confirm_account_by_email_without_address')}<br/>${getLabel('migration_spam_warning')}`,
                button_text: getLabel('migration_confirm_account_button'),
                onButtonClick: async () => {
                    if (!confirmCode || !confirmCode.trim()) {
                        setError({ message: getLabel('migration_confirm_code_required') });
                        return;
                    }
                    try {
                        setLoading(true);
                        const { email, temporaryPassword } = ctxRef.current;
                        if (smsSent || voiceCallSent) {
                            const res = await userApi.VerifySmsOtpAndResetPassword(email, confirmCode, temporaryPassword);
                            if (!res?.success) throw new Error(getLabel('otp_expired_or_invalid'));
                        } else {
                            // Use Cognito's confirmPassword to reset password for existing user
                            await confirmPassword(email, confirmCode, temporaryPassword);
                        }
                        // Password successfully reset → sign in and ensure state is updated
                        await signIn(email, temporaryPassword);

                        // Always call MigrateUserFromOldDB — covers both normal migration and
                        // users who had a Cognito account but whose DB migration previously failed.
                        // Returns AlreadyExists for already-migrated users (handled harmlessly).
                        const token = getJwtToken ? getJwtToken() : '';
                        const res = await userApi.MigrateUserFromOldDB(token);
                        const isOk = (res === true) || (res && (res.success === true || res.status === 'Success' || res.status === 'AlreadyExists'));
                        if (!isOk) {
                            const message = (res && (res.errorMessage || res.message)) || 'backend-migration-failed';
                            throw new Error(message);
                        }

                        try { sessionStorage.removeItem('migrationInProgress'); } catch (_) {}
                        pingUserData();
                        alertRef.current?.setShow(false);
                        setIsShown(false);
                        const lang = getLabel('lang') || 'he';
                        navigate(lang === 'he' ? '/' : `/${lang}`);
                    } catch (err) {
                        setError(err);
                    } finally {
                        setLoading(false);
                    }
                },
                preventCloseOnButton: true,
                content: (
                    <div className={styles.migrationFildes}>
                        <FormInputField
                            id="reset_confirm_code_field"
                            label={getLabel('login_form_confirm_code_field')}
                            placeholder={getLabel('login_form_confirm_code_field')}
                            type="text"
                            required={true}
                            heContentDirection={'ltr'}
                            hePlaceholderDirection={'rtl'}
                            value={confirmCode}
                            autoComplete="one-time-code"
                            name="confirmation-code"
                            inputMode="numeric"
                            onChange={(e) => setConfirmCode(e.target.value)}
                        />
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: '#ff3333', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error) }} />
                        </div>
                        {loading && (
                            <div style={{ marginTop: '10px' }}>
                                <Loader />
                            </div>
                        )}
                        <div className={styles.codeOptions}>
                            <button
                                type="button"
                                className={styles.codeOption}
                                disabled={loading || resendSent}
                                onClick={() => handleResendCode(() => sendResetPasswordCode(ctxRef.current.email))}
                            >
                                <RefreshIcon />
                                <span>{resendSent ? getLabel('migration_code_sent_confirmation') : getLabel('migration_resend_code_link')}</span>
                            </button>
                            {renderFallbackButtons()}
                        </div>
                    </div>
                )
            });
            return;
        }

        // default: password form
        alertRef.current.show({
            type: alertType.alert,
            title: isPasswordReset ? getLabel('login_form_forgot_password_title') : getLabel('migration_welcome_title'),
            text: isPasswordReset ? getLabel('login_form_new_password_required_password_text') : getLabel('migration_welcome_text'),
            button_text: isPasswordReset ? getLabel('login_form_forgot_password_send_message_button') : getLabel('migration_submit_button'),
            onButtonClick: onSubmit,
            preventCloseOnButton: true,
            content: (
                <div className={styles.migrationFildes}>
                    <FormInputField
                        id="migration_new_password_field"
                        label={getLabel('migration_new_password_field')}
                        placeholder={getLabel('migration_new_password_field')}
                        ref={passwordRef}
                        type="password"
                        required={true}
                        passwordValidation={true}
                        heContentDirection={'ltr'}
                        hePlaceholderDirection={'rtl'}
                    />
                    <FormInputField
                        id="migration_confirm_password_field"
                        label={getLabel('migration_confirm_password_field')}
                        placeholder={getLabel('migration_confirm_password_field')}
                        ref={confirmPasswordRef}
                        type="password"
                        required={true}
                        validationPattern={confirmPasswordValidation}
                        heContentDirection={'ltr'}
                        hePlaceholderDirection={'rtl'}
                    />
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ color: '#ff3333', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error) }} />
                    </div>
                    {loading && (
                        <div style={{ marginTop: '10px' }}>
                            <Loader />
                        </div>
                    )}
                </div>
            )
        });
    }, [confirmPasswordValidation, error, getCognitoApiError, getLabel, onSubmit, loading, view, completed, confirmCode, getJwtToken, isPasswordReset, resendSent, handleResendCode, resendConfirmationAccountCode, sendResetPasswordCode, pingUserData, navigate, renderFallbackButtons]);

    useEffect(() => {
        setSmsMaskedPhone(null);
        setVoiceMaskedPhone(null);
        setSmsSent(false);
        setSmsError(null);
        setVoiceCallSent(false);
        setVoiceCallError(null);
        if ((view === 'confirm' || view === 'reset_confirm') && ctxRef.current.email) {
            userApi.GetSmsOption(ctxRef.current.email)
                .then(res => { if (res?.hasPhone) setSmsMaskedPhone(res.maskedPhone); })
                .catch(() => {});
            userApi.GetVoiceOption(ctxRef.current.email)
                .then(res => { if (res?.hasPhone) setVoiceMaskedPhone(res.maskedPhone); })
                .catch(() => {});
        }
    }, [view]);

    useImperativeHandle(ref, () => ({
        open: (email, oldPassword, oldUser, passwordResetMode = false) => {
            const resolvedEmail = (oldUser && oldUser.email) ? oldUser.email : (email || '');
            ctxRef.current = { email: resolvedEmail, oldPassword: oldPassword || '', oldUser: oldUser || null, newPassword: '', temporaryPassword: '' };
            setError(null);
            setLoading(false);
            setCompleted(false);
            setView('form');
            setConfirmCode('');
            setResendSent(false);
            setIsPasswordReset(passwordResetMode);
            setSmsMaskedPhone(null);
            setVoiceMaskedPhone(null);
            setSmsSent(false);
            setSmsSending(false);
            setSmsError(null);
            setVoiceCallSent(false);
            setVoiceCallSending(false);
            setVoiceCallError(null);
            setIsShown(true);
            try { sessionStorage.setItem('migrationInProgress', '1'); } catch (_) {}
            renderAlert();
        },
        openConfirm: (email, newPassword) => {
            const resolvedEmail = email || '';
            ctxRef.current = { email: resolvedEmail, oldPassword: '', oldUser: null, newPassword: newPassword || '', temporaryPassword: '' };
            setError(null);
            setLoading(false);
            setCompleted(false);
            setView('confirm');
            setConfirmCode('');
            setResendSent(false);
            setIsPasswordReset(false);
            setSmsMaskedPhone(null);
            setVoiceMaskedPhone(null);
            setSmsSent(false);
            setSmsSending(false);
            setSmsError(null);
            setVoiceCallSent(false);
            setVoiceCallSending(false);
            setVoiceCallError(null);
            setIsShown(true);
            try { sessionStorage.setItem('migrationInProgress', '1'); } catch (_) {}
            renderAlert();
        }
    }), [renderAlert]);

    // Refresh content when error/loading changes while alert is open
    useEffect(() => {
        if (isShown) {
            renderAlert();
        }
    }, [error, loading, isShown, view, renderAlert]);

    // Removed auto-restore to prevent immediate confirm without login context

    const allowClose = (view === 'success') || completed || !!error;
    return <Alert ref={alertRef} showClose={allowClose} preventMaskClose={true} />;
});

export default MigrationAlert;


